import React, { useEffect, useRef, useState } from 'react';
import { func, object, bool, array, string } from 'prop-types';
import Button from '@Components/Button';
import Tooltip from '@Components/Tooltip';
import { initialUserForm, changeAccountType, validateUserForm, userFormPayload } from './userForm';
import { ACCOUNT_TYPES, accountTypeInfo, compatibleRoles, ROLES_HELP } from '@Config/accountTypes';
import './styles.scss';

const UserFormModal = ({ user = null, isVisible = false, onSave, onDismiss, loading = false, roles = [], rolesLoading = false, rolesError = '', canManage = true }) => {
  const [form, setForm] = useState(() => initialUserForm());
  const [errors, setErrors] = useState({});
  const [passwordOpen, setPasswordOpen] = useState(false);
  const formRef = useRef(null);
  useEffect(() => {
    setForm(initialUserForm(user));
    setErrors({});
    setPasswordOpen(!user);
  }, [user, isVisible]);
  useEffect(() => {
    if (Object.keys(errors).length) formRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [errors]);
  if (!isVisible) return null;
  const needsCuit = form.accountType === '1';
  const availableRoles = compatibleRoles(roles, form.accountType);
  const incompatible = form.roles.filter(name => !availableRoles.some(role => role.name === name));
  const typeInfo = accountTypeInfo(form.accountType);
  const change = (event) => {
    const { name, value, checked, type } = event.target;
    setForm(previous => name === 'accountType' ? changeAccountType(previous, value, roles) : ({ ...previous, [name]: type === 'checkbox' ? checked : value }));
    setErrors({});
  };
  const field = (name, label, type = 'text', disabled = false, maxLength = 100) => (
    <div className="col-md-6">
      <label htmlFor={`admin-${name}`} className="form-label">{label}</label>
      <input id={`admin-${name}`} name={name} type={type} className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
        value={form[name]} onChange={change} disabled={loading || disabled} maxLength={maxLength}
        aria-invalid={!!errors[name]} aria-describedby={errors[name] ? `admin-${name}-error` : undefined}
        autoComplete={type === 'password' ? 'new-password' : 'off'} />
      {errors[name] && <div id={`admin-${name}-error`} className="invalid-feedback">{errors[name]}</div>}
    </div>
  );
  const submit = (event) => {
    event.preventDefault();
    if (loading || rolesLoading || rolesError || !canManage || user?.accountTypeIssue) return;
    const validation = validateUserForm(form, !!user, roles);
    if (validation.password || validation.confirmPassword) setPasswordOpen(true);
    setErrors(validation);
    if (Object.keys(validation).length === 0) onSave(userFormPayload(form, user));
  };
  return (
    <div className="modal user-form-modal fade show d-block" role="dialog" aria-modal="true"
      aria-labelledby="user-form-modal-title" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"><div className="modal-content">
        <div className="modal-header">
          <div>
            <h5 id="user-form-modal-title" className="modal-title">{user ? 'Editar usuario' : 'Nuevo usuario'}</h5>
            <p className="user-form-subtitle">{user ? 'Administrá sus datos y permisos de acceso.' : 'Completá los datos y asigná los permisos de acceso.'}</p>
          </div>
          <button type="button" className="btn-close" onClick={onDismiss} disabled={loading} aria-label="Cerrar" />
        </div>
        <form ref={formRef} onSubmit={submit} noValidate>
          <div className="modal-body">
            {user?.accountTypeIssue && <div role="alert" className="alert alert-warning">Esta cuenta presenta una configuración de cuenta inconsistente. Requiere revisión administrativa antes de editarse.</div>}
            <fieldset disabled={!canManage || !!user?.accountTypeIssue} className="user-form-fields">
            <section className="user-form-section" aria-labelledby="user-account-heading">
              <h6 id="user-account-heading">Cuenta y permisos</h6>
              <div className="row g-3">
            <div className="col-md-6">
              <fieldset aria-describedby={user ? 'account-type-help account-type-description' : 'account-type-description'}>
                <legend className="user-account-legend">Tipo de cuenta</legend>
                <div className="user-account-options">
                  {ACCOUNT_TYPES.map(kind => <label key={kind.value} htmlFor={`admin-accountType-${kind.value}`}
                    className={`user-account-option ${form.accountType === String(kind.value) ? 'is-selected' : ''}`}>
                    <input id={`admin-accountType-${kind.value}`} type="radio" className="form-check-input" name="accountType"
                      value={kind.value} checked={form.accountType === String(kind.value)} onChange={change} disabled={loading || !!user} />
                    <span>{kind.label}</span>
                  </label>)}
                </div>
                {!typeInfo && <p className="form-text">Sin clasificar</p>}
                {user && <p id="account-type-help" className="form-text">El tipo de cuenta no puede modificarse después de crear el usuario.</p>}
              </fieldset>
              <p id="account-type-description" className="form-text">{typeInfo?.description}</p>
              {form.accountType === '2' && <p className="form-text">El rol Administrador tiene acceso total.</p>}
              {!user && needsCuit && <div className="form-check"><input id="admin-on-behalf" type="checkbox" className="form-check-input" name="onBehalf" checked={form.onBehalf} disabled={loading} onChange={change} /><label htmlFor="admin-on-behalf" className="form-check-label">Opera en representación de otro productor</label></div>}
              {errors.accountType && <div role="alert" className="text-danger">{errors.accountType}</div>}
            </div>
            <div className="col-md-6"><fieldset disabled={loading || rolesLoading}>
              <legend className="form-label float-none d-flex align-items-center gap-2">Rol asignado *
                <Tooltip text={ROLES_HELP} id="admin-roles-help">
                  <button type="button" className="btn btn-link p-0 border-0" aria-label="Información sobre el rol asignado">
                    <i className="bi bi-info-circle" aria-hidden="true" />
                  </button>
                </Tooltip>
              </legend>
              {form.roles.length > 1 && <p role="alert" className="alert alert-warning py-2">Este usuario tiene varios roles: {form.roles.join(', ')}. Seleccioná el único rol que conservará al guardar.</p>}
              {rolesLoading ? <p role="status">Cargando roles…</p> : rolesError ? <p role="alert" className="text-danger">{rolesError}</p> : <>
                {!availableRoles.length && <p>No hay roles compatibles configurados para este tipo de cuenta.</p>}
                <div className="user-roles-list">{availableRoles.map(role => <div className="form-check" key={role.id}><input id={`admin-role-${role.id}`} type="radio" name="assignedRole" value={role.name} className="form-check-input" checked={form.roles.length === 1 && form.roles[0] === role.name}
                  aria-invalid={!!errors.roles} aria-describedby={errors.roles ? 'admin-roles-error' : undefined} onChange={() => {
                  setForm(previous => ({ ...previous, roles: [role.name] })); setErrors({});
                }} /><label htmlFor={`admin-role-${role.id}`} className="form-check-label">{role.name}</label></div>)}</div>
                {!!incompatible.length && <p role="alert" className="text-danger">Roles incompatibles: {incompatible.join(', ')}. Revise la configuración del rol antes de editar esta cuenta.</p>}
              </>}
              {errors.roles && <div id="admin-roles-error" role="alert" className="text-danger">{errors.roles}</div>}
            </fieldset></div>
              </div>
            </section>
            <section className="user-form-section" aria-labelledby="user-contact-heading">
              <h6 id="user-contact-heading">Datos personales y de contacto</h6>
              <div className="row g-3">
            {needsCuit && field('cuit', 'CUIT / CUIL *', 'text', !!user, 13)}
            {needsCuit && form.onBehalf && field('producerCuit', 'CUIT del productor responsable *', 'text', false, 13)}
            {field('email', 'Correo electrónico *', 'email')}
            {field('firstName', 'Nombre / Razón social *', 'text', false, 50)}
            {field('lastName', 'Apellido *', 'text', false, 50)}
            {field('phoneNumber', 'Teléfono', 'tel', false, 20)}
            {!user && !needsCuit && <p className="form-text mb-0">El correo electrónico será el nombre de usuario para ingresar.</p>}
              </div>
            </section>
            <section className="user-form-section" aria-labelledby="user-access-heading">
              <h6 id="user-access-heading">Acceso y seguridad</h6>
              <div className="row g-3 align-items-center">
            <div className="col-md-6">
              <label htmlFor="admin-status" className="form-label">Estado</label>
              <select id="admin-status" name="status" className="form-select" value={form.status} onChange={change} disabled={loading}>
                <option value="active">Activo</option><option value="blocked">Bloqueado</option>
              </select>
            </div>
            <div className="col-md-6"><div className="form-check">
              <input id="admin-emailConfirmed" name="emailConfirmed" type="checkbox" className="form-check-input"
                checked={form.emailConfirmed} onChange={change} disabled={loading} />
              <label htmlFor="admin-emailConfirmed" className="form-check-label">Email confirmado</label>
            </div></div>
              </div>
              <div className="user-password-section">
                {user ? <button type="button" className="user-password-toggle" aria-expanded={passwordOpen}
                  aria-controls="admin-password-fields" onClick={() => setPasswordOpen(open => !open)} disabled={loading}>
                  Cambiar contraseña <span aria-hidden="true">{passwordOpen ? '−' : '+'}</span>
                </button> : <h6>Contraseña</h6>}
                <p id="admin-password-help" className="form-text">{user ? 'Completá ambos campos solo si necesitás cambiar la contraseña.' : 'Usá al menos 6 caracteres y repetí la contraseña para confirmarla.'}</p>
                <div id="admin-password-fields" hidden={!passwordOpen} aria-describedby="admin-password-help">
                  <div className="row g-3">
            {field('password', user ? 'Nueva contraseña (opcional)' : 'Contraseña *', 'password')}
            {field('confirmPassword', 'Confirmar contraseña', 'password')}
                  </div>
                </div>
              </div>
            </section>
          </fieldset></div>
          <div className="modal-footer">
            <span className="user-form-required">* Campos obligatorios</span>
            <Button type="button" color="secondary" size="smaller" onClick={onDismiss} disabled={loading}>Cancelar</Button>
            <Button type="submit" color="primary" size="smaller" disabled={loading || rolesLoading || !!rolesError || !canManage || !!user?.accountTypeIssue || !typeInfo || !!incompatible.length || form.roles.length > 1}>{loading ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        </form>
      </div></div>
    </div>
  );
};
UserFormModal.propTypes = { user: object, isVisible: bool, onSave: func.isRequired, onDismiss: func.isRequired, loading: bool, roles: array, rolesLoading: bool, rolesError: string, canManage: bool };
export default React.memo(UserFormModal);
